import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { DownloadOutlined } from '@mui/icons-material';
import { EquityLines, MonthlyBars, Series } from '../components/charts';
import { EmptyRow, Kpi, PageHeader, Panel, Pnl, inr, num } from '../components/ui';
import BacktestPanel from '../components/BacktestPanel';
import { errorMessage, tradingApi } from '../services/api';
import { CurveStats, HistoryData, HistoryMode, Trade } from '../types';
import { t } from '../utils/theme';

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const RANGES = [{ d: 90, label: '3M' }, { d: 365, label: '1Y' }, { d: 1095, label: '3Y' }, { d: 5000, label: 'All' }];
const pct = (v: number | undefined | null, signed = true) => (v == null ? '—' : `${signed && v > 0 ? '+' : ''}${v.toFixed(1)}%`);

function downloadCsv(trades: Trade[], mode: HistoryMode) {
  const head = ['bought', 'sold', 'symbol', 'qty', 'buy_price', 'sell_price', 'rank_at_buy', 'reason', 'charges', 'net_pnl'];
  const rows = trades.map((x) => [
    new Date(x.entry_time).toLocaleDateString('en-CA'), x.exit_time ? new Date(x.exit_time).toLocaleDateString('en-CA') : '',
    x.symbol, x.qty, x.entry_price, x.exit_price ?? '', x.entry_rank ?? '', x.exit_reason, x.charges, x.pnl,
  ]);
  const csv = [head, ...rows].map((r) => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `trades-${mode.toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const StatCell: React.FC<{ label: string; strategy: React.ReactNode; benchmark?: React.ReactNode }> = ({ label, strategy, benchmark }) => (
  <Kpi label={label} value={strategy} sub={benchmark !== undefined ? <>NIFTY 50: {benchmark}</> : undefined} />
);

const History: React.FC = () => {
  const [days, setDays] = useState(365);
  const [mode, setMode] = useState<HistoryMode | null>(null);
  const [data, setData] = useState<HistoryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [reload, setReload] = useState(0);
  const backtest = mode === 'BACKTEST';

  useEffect(() => {
    tradingApi.config().then((c) => setMode(c.mode)).catch(() => setMode('PAPER'));
  }, []);

  useEffect(() => {
    if (!mode) return;
    setData(null);
    setError(null);
    setPage(0);
    tradingApi.history(backtest ? 5000 : days, mode).then(setData).catch((e) => setError(errorMessage(e, 'Could not load history')));
  }, [days, mode, backtest, reload]);

  const series: Series[] = useMemo(() => {
    if (!data) return [];
    const s: Series[] = [{ name: 'Strategy', color: t.accentSoft, points: data.curve.map((p) => ({ day: p.day, value: Number(p.equity) })) }];
    const bench = data.benchmark?.benchmark_curve;
    if (backtest && bench?.length) s.push({ name: 'NIFTY 50 (buy & hold)', color: t.text3, points: bench.map(([d, v]) => ({ day: d, value: Number(v) })) });
    return s;
  }, [data, backtest]);

  const eq: CurveStats = data?.equity ?? {};
  const bm: CurveStats | undefined = backtest ? data?.benchmark?.benchmark : undefined;
  const s = data?.summary;
  const hasCurve = (data?.curve.length ?? 0) > 1;

  return (
    <Box>
      <PageHeader
        title="History"
        subtitle="Portfolio value over time, monthly P&L and every completed holding. All figures are net of estimated charges."
        actions={
          <>
            <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)} aria-label="Mode">
              <ToggleButton value="PAPER">Paper</ToggleButton>
              <ToggleButton value="LIVE">Live</ToggleButton>
              <ToggleButton value="BACKTEST">Backtest</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)} aria-label="Period" disabled={backtest}>
              {RANGES.map((r) => <ToggleButton key={r.d} value={r.d}>{r.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <Button size="small" variant="outlined" startIcon={<DownloadOutlined />} disabled={!data?.trades.length}
                    onClick={() => data && downloadCsv(data.trades, data.mode)}>
              CSV
            </Button>
          </>
        }
      />

      {backtest && <BacktestPanel onFinished={() => setReload((n) => n + 1)} />}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!data && !error && (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={110} /></Grid>)}
          <Grid item xs={12}><Skeleton variant="rounded" height={280} /></Grid>
        </Grid>
      )}

      {data && s && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCell label="Total return" strategy={<Pnl value={eq.start && eq.end ? Number(eq.end) - Number(eq.start) : null} size="md" />}
                        benchmark={bm ? pct(bm.total_return_pct) : undefined} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCell label="CAGR" strategy={<span style={{ color: (eq.cagr_pct ?? 0) >= 0 ? t.profit : t.loss }}>{pct(eq.cagr_pct)}</span>}
                        benchmark={bm ? pct(bm.cagr_pct) : undefined} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCell label="Max drawdown" strategy={<span style={{ color: t.loss }}>{eq.max_drawdown_pct == null ? '—' : `−${eq.max_drawdown_pct.toFixed(1)}%`}</span>}
                        benchmark={bm ? `−${(bm.max_drawdown_pct ?? 0).toFixed(1)}%` : undefined} />
            </Grid>
            <Grid item xs={6} md={3}>
              <Kpi label="Completed holdings" value={String(s.trades)}
                   sub={s.trades ? `${s.win_rate}% profitable · profit factor ${s.profit_factor ?? '—'}` : 'None yet'} />
            </Grid>
          </Grid>

          {hasCurve ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={7}>
                <Panel title="Portfolio value" subtitle={`${fmtDate(data.curve[0].day)} – ${fmtDate(data.curve[data.curve.length - 1].day)}${eq.years ? ` · ${eq.years} years` : ''}`}>
                  <EquityLines series={series} />
                </Panel>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Panel title="Monthly P&L" subtitle="Change in portfolio value each month">
                  <MonthlyBars data={data.monthly.map((m) => ({ month: m.month, pnl: Number(m.pnl) }))} />
                </Panel>
              </Grid>
            </Grid>
          ) : (
            <Panel>
              <Typography sx={{ color: t.text2, textAlign: 'center', py: 4 }}>
                {backtest ? 'No backtest results yet. Pick a period above and press Run backtest.'
                  : `No ${data.mode.toLowerCase()} history yet. The chart fills in after the first rebalance and daily valuations.`}
              </Typography>
            </Panel>
          )}

          {data.trades.length > 0 && (
            <Panel title="Completed holdings" subtitle={`${data.trades.length} round trips, newest first`} pad={false}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bought</TableCell><TableCell>Sold</TableCell><TableCell>Stock</TableCell><TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Buy</TableCell><TableCell align="right">Sell</TableCell><TableCell>Why sold</TableCell>
                      <TableCell align="right">Charges</TableCell><TableCell align="right">Net P&L</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.trades.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((x) => (
                      <TableRow key={x.id} hover>
                        <TableCell sx={{ color: t.text2, whiteSpace: 'nowrap' }}>{fmtDate(x.entry_time)}</TableCell>
                        <TableCell sx={{ color: t.text2, whiteSpace: 'nowrap' }}>{x.exit_time ? fmtDate(x.exit_time) : '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{x.symbol.replace('NSE:', '')} <span style={{ color: t.text3, fontWeight: 400 }}>#{x.entry_rank ?? '—'}</span></TableCell>
                        <TableCell align="right">{x.qty}</TableCell>
                        <TableCell align="right">{num(x.entry_price)}</TableCell>
                        <TableCell align="right">{num(x.exit_price)}</TableCell>
                        <TableCell sx={{ color: t.text2 }}>{x.exit_reason.toLowerCase().replace('_', ' ')}</TableCell>
                        <TableCell align="right" sx={{ color: t.text2 }}>{num(x.charges)}</TableCell>
                        <TableCell align="right"><Pnl value={x.pnl} /></TableCell>
                      </TableRow>
                    ))}
                    {!data.trades.length && <EmptyRow cols={9} text="No completed holdings" />}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={data.trades.length} page={page} onPageChange={(_, p) => setPage(p)}
                               rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                               rowsPerPageOptions={[25, 50, 100]} />
            </Panel>
          )}
          {!backtest && data.trades.length === 0 && hasCurve && (
            <Typography variant="body2" sx={{ color: t.text3 }}>Realised P&L shows here once a holding is sold. Total P&L so far: {inr(s.total_pnl, true)}.</Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default History;
